/**
 * WAT-11458: single source of truth for the exit modal's `frequencyHours` CMS field.
 *
 * Two call sites gate on this one authored value and MUST agree, or a cooldown exists
 * in one place and not the other:
 *   - `ModalOrchestrator.isExitModalEligible` — decides whether DtvExitModal MOUNTS at all
 *   - `DtvExitModal` — decides whether an already-mounted modal RECURS past its visit window
 *
 * They previously normalised the field independently (`parseInt` + `Number.isFinite` in the
 * component, `Number()` + an explicit unset check in the orchestrator) and diverged on real
 * authored shapes: `"48hrs"` resolved to 48 in one and the 24h default in the other, `"6.5"`
 * to 6 vs 6.5. Worse, an authored `0` came out as "never recur" in the component (falsy, so
 * its recurrence branches were skipped) and "always eligible" in the orchestrator
 * (`hoursSince < 0` is never true, so the cooldown fell through) — opposite readings of the
 * one value the WAT-11458 zero-handling contract is built on.
 *
 * The contract this encodes:
 *   unset (`undefined` / `null` / `""`, Storyblok's empty number field) -> `fallback` (24h)
 *   unparseable (`"48hrs"`, `NaN`)                                      -> `fallback` (24h)
 *   a positive number (`"6.5"`, `24`)                                   -> that number, as authored
 *   `0` or negative                                                     -> `0`, meaning NEVER RECUR
 *
 * `0` is normalised for BOTH zero and negatives so a single falsy check expresses
 * "never recur" at every call site — a raw negative is truthy and would otherwise sail
 * through the component's `&& frequencyHours` guards and recur on every visit.
 *
 * Malformed input resolves to the default rather than to its leading digits: an author
 * typing `"48hrs"` has made a mistake, and defaulting is predictable where guessing is not.
 */
export const normalizeFrequencyHours = (raw: unknown, fallback = 24): number => {
  if (raw === undefined || raw === null || raw === "") {
    return fallback;
  }
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return parsed > 0 ? parsed : 0;
};
